import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useRef } from 'react';
import { searchMobileDevices, UserKeys } from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Messages } from '../../../utils/message-constants';
import MobileManagementGrid from './mobile-management-grid';
import { useForm } from 'react-hook-form';
import MobileSearchGridFilters from './mobile-search-filters';
import dayjs from 'dayjs';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { MobileDevice, MobileSearchFilter } from '../../../../types';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import MobileDeviceGrid from '../../user-management/user-details/devices/mobile-device-grid';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';
import MobileDevicesIndexedGrid from './mobile-devices-indexed-grid';
import Stack from '@mui/material/Stack';

export interface MobileSearchFormFilter {
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  email: string;
  status:
    | 'pending'
    | 'approved'
    | 'account_wiped'
    | 'blocked'
    | 'unprovisioned'
    | 'wiped'
    | 'all';
  type: 'android' | 'ios' | 'both';
  syncType: 'inclusiveRange' | 'beforeDate' | 'afterDate';
  syncStartDate: dayjs.Dayjs | null;
  syncEndDate: dayjs.Dayjs | null;
}

export default function MobileManagementPage() {
  const { userRoles, toolsRoles, changeLogRoles, viewType } = useUserSettings();
  const fetched = useRef(false);
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const { features } = useUserSettings();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
    reset,
  } = useForm<MobileSearchFormFilter>({
    defaultValues: {
      email: '',
      status: 'all',
      type: 'both',
      syncStartDate: null,
      syncEndDate: null,
      syncType: 'inclusiveRange',
      dateRange: [null, null],
    },
  });

  const searchedMobileDevices: MobileDevice[] | undefined =
    queryClient.getQueryData([UserKeys.SearchedMobileDevices]);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: MobileSearchFilter) => searchMobileDevices(data),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Devices found successfully!',
      });
      queryClient.setQueryData<MobileDevice[]>(
        [UserKeys.SearchedMobileDevices],
        data
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          Messages.Errors.Network.Searching('for mobile devices'),
      });
    },
  });

  if (isPending) {
    fetched.current = true;
  }

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.devices.name },
        { label: pagesConfig.mobileManagement.name },
      ]}
      bookmarkKey={pagesConfig.mobileManagement.value}
    >
      {isFeatureEnabled('showMobileManagementFilters', features) ? (
        <Stack sx={{ height: '100%' }} gap={2} flexDirection={'row'}>
          <MobileSearchGridFilters
            control={control}
            watch={watch}
            setValue={setValue}
            isLoading={isPending}
            mutate={mutate}
            handleSubmit={handleSubmit}
            isDirty={isDirty}
            reset={reset}
          />
          <MobileManagementGrid
            devices={searchedMobileDevices || []}
            toolsRoles={toolsRoles}
            changeLogRoles={changeLogRoles}
            isLoading={isPending}
            fetched={fetched.current}
          />
        </Stack>
      ) : isFeatureEnabled('indexMobileDevicesReact', features) ? (
        <MobileDevicesIndexedGrid
          gridKey={viewType.mobileManagementIndexedGridStates?.activeGridState}
          userRoles={userRoles}
          toolsRoles={toolsRoles}
          changeLogRoles={changeLogRoles}
          features={features}
        />
      ) : (
        <MobileDeviceGrid
          key={viewType.mobileManagementGridStates?.activeGridState}
          userRoles={userRoles}
          toolsRoles={toolsRoles}
          changeLogRoles={changeLogRoles}
        />
      )}
    </SimpleBreadcrumbsPage>
  );
}
